/**
 * InsightsSection Component
 *
 * AI-powered insights panel that generates dynamic analysis using Gemini.
 * Shows performance insights based on score changes and CAMEL components.
 * Includes a "View Full Report" button to navigate to the detailed report.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Lightbulb,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle,
  AlertTriangle,
  FileText,
} from 'lucide-react';
import type { HistoricalPeriod, Insight, CAMELCode } from '../../types';
import { camelLabels } from '../../constants';
import { getRatingIndex } from '../../utils';
import { getIdToken } from '../../../../services/auth';
import styles from './InsightsSection.module.css';

export interface InsightsSectionProps {
  /** Institution ID for API calls and navigation */
  institutionId: string;
  /** Currently selected period data */
  currentPeriod: HistoricalPeriod;
  /** Previous period for comparison */
  previousPeriod: HistoricalPeriod | null;
}

// Generate local insights (fallback when API is not available)
function generateLocalInsights(
  currentPeriod: HistoricalPeriod,
  previousPeriod: HistoricalPeriod | null,
  t: (key: string, options?: any) => string
): Insight[] {
  const results: Insight[] = [];

  // Score change insight
  if (previousPeriod) {
    const scoreDiff = currentPeriod.finalScore - previousPeriod.finalScore;
    if (Math.abs(scoreDiff) > 1) {
      results.push({
        type: scoreDiff > 0 ? 'positive' : 'negative',
        title: scoreDiff > 0 ? t('insights.scoreImprovement') : t('insights.scoreDecline'),
        description: t('insights.scoreChanged', { direction: t(scoreDiff > 0 ? 'insights.increased' : 'insights.decreased'), delta: Math.abs(scoreDiff).toFixed(2) }),
      });
    }
  }

  // Rating change insight
  if (previousPeriod && currentPeriod.rating !== previousPeriod.rating) {
    const currentIdx = getRatingIndex(currentPeriod.rating);
    const prevIdx = getRatingIndex(previousPeriod.rating);
    results.push({
      type: currentIdx > prevIdx ? 'positive' : 'negative',
      title: t('insights.ratingChange'),
      description: t('insights.ratingChanged', { direction: t(currentIdx > prevIdx ? 'insights.upgraded' : 'insights.downgraded'), from: previousPeriod.rating, to: currentPeriod.rating }),
    });
  }

  // CAMEL performance insights
  const camelPerformance = currentPeriod.camel
    .map((c) => ({
      ...c,
      ratio: c.score / camelLabels[c.code as CAMELCode].max,
    }))
    .sort((a, b) => b.ratio - a.ratio);

  const best = camelPerformance[0];
  const worst = camelPerformance[camelPerformance.length - 1];

  if (best && best.ratio >= 0.9) {
    results.push({
      type: 'positive',
      title: t('insights.strongPerformance'),
      description: t('insights.performingExcellently', { component: camelLabels[best.code as CAMELCode].name, percentage: (best.ratio * 100).toFixed(0) }),
    });
  }

  if (worst && worst.ratio < 0.5) {
    results.push({
      type: 'warning',
      title: t('insights.areaOfConcern'),
      description: t('insights.needsAttention', { component: camelLabels[worst.code as CAMELCode].name, percentage: (worst.ratio * 100).toFixed(0) }),
    });
  }

  // Liquidity insight
  const liquidity = currentPeriod.camel.find((c) => c.code === 'L');
  if (liquidity && liquidity.score / camelLabels['L'].max > 0.9) {
    results.push({
      type: 'positive',
      title: t('insights.strongLiquidity'),
      description: t('insights.liquidityRobust'),
    });
  }

  // If no insights generated, show stable message
  if (results.length === 0) {
    results.push({
      type: 'neutral',
      title: t('insights.stablePerformance'),
      description: t('insights.noSignificantChanges'),
    });
  }

  return results.slice(0, 4);
}

// Calculate CAMEL changes for the API
function calculateCamelChanges(
  current: HistoricalPeriod,
  previous: HistoricalPeriod | null
): Record<string, number> | null {
  if (!previous) return null;

  const changes: Record<string, number> = {};
  current.camel.forEach((c) => {
    const prev = previous.camel.find((p) => p.code === c.code);
    if (prev) {
      changes[c.code] = parseFloat((c.score - prev.score).toFixed(2));
    }
  });
  return changes;
}

const iconMap = {
  positive: ArrowUpRight,
  negative: ArrowDownRight,
  warning: AlertTriangle,
  neutral: CheckCircle,
};

export function InsightsSection({
  institutionId,
  currentPeriod,
  previousPeriod,
}: InsightsSectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [insights, setInsights] = useState<Insight[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch insights from API or fall back to local generation
  const fetchInsights = useCallback(async () => {
    if (!institutionId || !currentPeriod) return;

    setIsLoading(true);
    setError(null);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';
      const token = await getIdToken();

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${apiUrl}/api/insights/generate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          institutionId,
          period: currentPeriod.date,
          currentScore: currentPeriod.finalScore,
          previousScore: previousPeriod?.finalScore,
          rating: currentPeriod.rating,
          previousRating: previousPeriod?.rating,
          camelChanges: calculateCamelChanges(currentPeriod, previousPeriod),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch insights');
      }

      const data = await response.json();
      setInsights(data.insights || []);
    } catch (err) {
      console.warn('API insights failed, using local generation:', err);
      // Fall back to local generation
      const localInsights = generateLocalInsights(currentPeriod, previousPeriod, t);
      setInsights(localInsights);
    } finally {
      setIsLoading(false);
    }
  }, [institutionId, currentPeriod, previousPeriod]);

  // Fetch insights when period changes
  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const handleViewReport = () => {
    if (institutionId) {
      navigate(`/reports?institution=${institutionId}&period=${currentPeriod.date}`);
    }
  };

  return (
    <div className={styles.insightsSection}>
      <div className={styles.insightsHeader}>
        <div className={styles.insightsTitle}>
          <Lightbulb size={18} />
          <span>{t('insights.title')}</span>
        </div>
        <button className={styles.viewReportButton} onClick={handleViewReport}>
          <FileText size={14} />
          {t('insights.viewFullReport')}
        </button>
      </div>

      {isLoading ? (
        <div className={styles.loadingSkeleton}>
          {[1, 2, 3].map((i) => (
            <div key={i} className={styles.skeletonItem} />
          ))}
        </div>
      ) : error ? (
        <div className={styles.errorState}>
          <AlertTriangle size={16} />
          <span>{error}</span>
        </div>
      ) : (
        <div className={styles.insightsList}>
          {insights.map((insight, i) => {
            const IconComponent = iconMap[insight.type];
            return (
              <div key={i} className={`${styles.insightItem} ${styles[insight.type]}`}>
                <div className={styles.insightIcon}>
                  <IconComponent size={16} />
                </div>
                <div className={styles.insightContent}>
                  <span className={styles.insightTitle}>{insight.title}</span>
                  <span className={styles.insightDescription}>{insight.description}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default InsightsSection;
